/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 * @param {Function} func The function to debounce
 * @param {number} wait The number of milliseconds to delay
 * @returns {Function} The debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttles a function to only execute once within a specified time period
 * @param {Function} func The function to throttle
 * @param {number} limit The time limit in milliseconds
 * @returns {Function} The throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Creates a memoized version of an expensive function
 * @param {Function} fn The function to memoize
 * @returns {Function} The memoized function
 */
export const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) return cache.get(key);
    const result = fn.apply(this, args);
    cache.set(key, result);
    return result;
  };
};

/**
 * Format a dollar amount with thousands separators (e.g. 1000 -> $1,000.00)
 * @param {number} amount - Amount in dollars
 * @param {object} [options] - { minDecimals, maxDecimals }
 * @returns {string}
 */
export const formatCurrencyDollars = (amount, options = {}) => {
  const { minDecimals = 2, maxDecimals = 2 } = options;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals
  }).format(Number(amount) || 0);
};

/**
 * Set up fallback: if user is still on page after ~2.5s (didn't open app), redirect to midus.co.
 * Use with <a href="midus://"> - the anchor triggers the app, this handles the fallback.
 * @param {string} [path=''] - Optional path to append to midus.co (e.g. '/chat/123')
 */
export const openMidusOrWeb = (path = '') => {
  const webUrl = `https://midus.co${path}`;
  const timeoutId = setTimeout(() => {
    if (!document.hidden) window.location.href = webUrl;
  }, 2500);
  const cancel = () => {
    if (document.hidden) {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', cancel);
    }
  };
  document.addEventListener('visibilitychange', cancel);
}; 